// dependencies
import React, { useEffect } from "react";

// components
import Button from "../../../components/Button";
import CloseButton from "../../../components/Button/templates/CloseButton";
import EmployeePicker, { SingleEmployee } from "../../../components/EmployeePicker";
import HashtagPicker from "../../../components/HashtagPicker";
import DatePicker from "../../../components/Inputs/DatePicker";
import SelectInput, { Result } from "../../../components/Inputs/SelectInput";
import Panel, { PanelProps } from "../../../components/Panel";
import Spacer from "../../../components/Spacer";
import Typography from "../../../components/Text";

// css
import css from "./filters.module.scss";

const removeKey = (filters: CompanyFeedbackFilter, key: string) => {
	const tempFilters = { ...filters };
	delete tempFilters[key];
	return tempFilters;
};

export type CompanyFeedbackFilter = {
	author?: SingleEmployee;
	subject?: SingleEmployee;
	sharedWith?: { value: string; label: string };
	content?: string;
	responseToRequest?: { value: string; label: string };
	hashTags?: Result[];
	dateBefore?: Date;
	dateAfter?: Date;
};

interface BaseProps extends PanelProps {
	filters: CompanyFeedbackFilter;
	onChange: (filters: CompanyFeedbackFilter) => void;
	onClose: () => void;
}

export const SHARED_WITH_OPTIONS = [
	{ label: "Any", value: undefined },
	{ label: "Everyone", value: "everyone" },
	{ label: "Subject", value: "subject" },
	{ label: "Subject's Manager", value: "subject-manager" },
	{ label: "Subject and Subject's Manager", value: "subject-+-manager" },
];

export const RESPONSE_TO_REQUEST_OPTIONS = [
	{ label: "Not Selected", value: undefined },
	{ label: "Yes", value: "true" },
	{ label: "No", value: "false" },
];

const CompanyFeedbackFilters = ({ isOpen, onClose, filters = {}, onChange = () => {} }: BaseProps) => {
	const [localFilters, setLocalFilters] = React.useState<CompanyFeedbackFilter>({ ...filters });

	useEffect(() => {
		setLocalFilters({ ...filters });
	}, [isOpen]);

	return (
		<Panel isOpen={isOpen} onClose={onClose} side="left">
			<div className={css.filters}>
				<div className={css.headerRow}>
					<Typography weight="semi-bold" size="sub-heading" color="neutral-10">
						Apply Filters
					</Typography>
					<CloseButton onClick={onClose} />
				</div>
				<>
					<Spacer height={3} />
					<EmployeePicker
						label="Subject"
						value={localFilters?.subject || ""}
						onChange={(newEmployeeValue) => {
							if (!Array.isArray(newEmployeeValue)) {
								const newFilters = newEmployeeValue
									? { ...localFilters, subject: newEmployeeValue }
									: removeKey(localFilters, "subject");
								setLocalFilters(newFilters as CompanyFeedbackFilter);
							}
						}}
						isClearable
					/>
				</>
				<>
					<Spacer height={1} />
					<EmployeePicker
						label="Author"
						value={localFilters?.author || ""}
						onChange={(newEmployeeValue) => {
							if (!Array.isArray(newEmployeeValue)) {
								const newFilters = newEmployeeValue
									? { ...localFilters, author: newEmployeeValue }
									: removeKey(localFilters, "author");
								setLocalFilters(newFilters as CompanyFeedbackFilter);
							}
						}}
						isClearable
					/>
				</>
				<>
					<Spacer height={1} />
					<SelectInput
						label="Shared With"
						results={SHARED_WITH_OPTIONS}
						value={
							localFilters?.sharedWith?.value ||
							SHARED_WITH_OPTIONS.find((option) => option.value === null)
						}
						onChange={({ value, label }) => {
							if (value) setLocalFilters({ ...localFilters, sharedWith: { value, label } });
						}}
					/>
				</>
				<>
					<Spacer height={1} />
					<div className={css.headerRow}>
						<Typography weight="semi-bold" size="paragraph-3" element="label" htmlFor="date-after-filter">
							Date After
						</Typography>
						<Button
							onClick={() => setLocalFilters(removeKey(localFilters, "dateAfter"))}
							size="micro"
							color="red"
							testid="clear-date-after"
							aria-label="Clear date after filter"
						>
							Clear
						</Button>
					</div>
					<Spacer height={1} />
				</>
				<DatePicker
					inputId="date-after-filter"
					name="Date After"
					value={localFilters?.dateAfter || ""}
					onChange={(value) => value && setLocalFilters({ ...localFilters, dateAfter: value })}
				/>
				<>
					<Spacer height={1} />
					<div className={css.headerRow}>
						<Typography weight="semi-bold" size="paragraph-3" element="label" htmlFor="date-before-filter">
							Date Before
						</Typography>
						<Button
							onClick={() => setLocalFilters(removeKey(localFilters, "dateBefore"))}
							size="micro"
							color="red"
							testid="clear-date-before"
							aria-label="Clear date before filter"
						>
							Clear
						</Button>
					</div>
					<Spacer height={1} />
				</>
				<DatePicker
					inputId="date-before-filter"
					name="Date Before"
					value={localFilters?.dateBefore || ""}
					onChange={(value) => value && setLocalFilters({ ...localFilters, dateBefore: value })}
				/>
				<>
					<Spacer height={1} />
					<HashtagPicker
						label={"Hashtags"}
						value={localFilters?.hashTags || []}
						isMultiSelect
						onChange={(value) =>
							setLocalFilters(
								value?.length
									? { ...localFilters, hashTags: value }
									: removeKey(localFilters, "hashTags")
							)
						}
					/>
				</>
				<>
					<Spacer height={1} />
					<SelectInput
						label="Was Requested"
						results={RESPONSE_TO_REQUEST_OPTIONS}
						value={
							localFilters?.responseToRequest ||
							RESPONSE_TO_REQUEST_OPTIONS.find((option) => option.value === null)
						}
						onChange={({ value, label }) => {
							setLocalFilters({ ...localFilters, responseToRequest: { value, label } });
						}}
					/>
				</>
				<Spacer height={2} />
				<Button
					onClick={() => {
						const appliedFilters = { ...localFilters };

						//remove any stuck but empty filters
						Object.keys(appliedFilters).forEach((key) => {
							if (!appliedFilters[key] || appliedFilters[key] === "") {
								delete appliedFilters[key];
							}
						});

						onChange({ ...appliedFilters });
						if (onClose) {
							onClose();
						}
					}}
				>
					Apply Filters
				</Button>
			</div>
		</Panel>
	);
};

export default CompanyFeedbackFilters;
